/**
 * ES6 module wrapper around the embedded canvas-to-tiff library.
 * This approach provides TypeScript support and a modern Promise-based interface for the TIFF conversion functionality.
 */

import CanvasToTIFF from './canvasToTiffLib.js';

export interface TiffOptions {
  /** DPI for both X and Y directions (default: 96) */
  dpi?: number;
  /** DPI for X direction (overrides dpi if provided) */
  dpiX?: number;
  /** DPI for Y direction (overrides dpi if provided) */
  dpiY?: number;
  /** Use little-endian format (default: false - big-endian) */
  littleEndian?: boolean;
}

/**
 * Converts an HTML5 Canvas to a TIFF Blob
 * @param canvas The canvas element to convert
 * @param options Optional TIFF encoding options
 * @returns Promise that resolves to a TIFF Blob
 */
export function convertCanvasToTiff(
  canvas: HTMLCanvasElement,
  options: TiffOptions = {}
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    try {
      CanvasToTIFF.toBlob(
        canvas,
        (blob: Blob) => {
          resolve(blob);
        },
        options
      );
    } catch (error) {
      reject(error);
    }
  });
}

/**
 * Converts an HTML5 Canvas to a TIFF ArrayBuffer
 * @param canvas The canvas element to convert
 * @param options Optional TIFF encoding options
 * @returns Promise that resolves to an ArrayBuffer containing TIFF data
 */
export function convertCanvasToTiffArrayBuffer(
  canvas: HTMLCanvasElement,
  options: TiffOptions = {}
): Promise<ArrayBuffer> {
  return new Promise((resolve, reject) => {
    try {
      CanvasToTIFF.toArrayBuffer(
        canvas,
        (buffer: ArrayBuffer) => {
          resolve(buffer);
        },
        options
      );
    } catch (error) {
      reject(error);
    }
  });
}

/**
 * Converts an HTML5 Canvas to a TIFF Data URL
 * @param canvas The canvas element to convert
 * @param options Optional TIFF encoding options
 * @returns Promise that resolves to a Data URL string
 */
export function convertCanvasToTiffDataUrl(
  canvas: HTMLCanvasElement,
  options: TiffOptions = {}
): Promise<string> {
  return new Promise((resolve, reject) => {
    try {
      CanvasToTIFF.toDataURL(
        canvas,
        (dataUrl: string) => {
          resolve(dataUrl);
        },
        options
      );
    } catch (error) {
      reject(error);
    }
  });
}
